
  import { defineComponent, ref, Ref, PropType, watch, onMounted, computed } from "vue";
  import { useStore } from "vuex";
  import {
    updateStoreValueAndFilter,
    handleVehicleSelected,
    addNewValueToFunnelStep,
    useShowElementByPredefinedManufacturer,
    callPaq,
    validation,
    getCarsBelowThreshold, getInputsAboveOrder, resetInputs, setResettingFormToFalseWhenNoRequests
  } from './../../Composables/form';
  import { isEmpty, arraysAreEqual } from './../../Composables/functions';
  import { FormActions, FormGetters } from './../../Store/index';
  import { CarItem } from './../../Store/form/types';

  import RadioComponent from "./RadioComponent.vue";

  import {ElIcon, ElInput, ElSelect, ElOption, ElOptionGroup} from "element-plus";
  import { Check, WarnTriangleFilled } from "@element-plus/icons-vue";

  interface Item {
    v: string;
    k: string;
  }

  interface Option {
    value: string;
    label: string;
    options: Array<{
      value: string;
      label: string;
    }>;
  }

  export default defineComponent({
    name: "SelectComponent",
    components: {
      ElInput,
      ElSelect,
      ElOption,
      ElOptionGroup,
      RadioComponent,
      ElIcon,
      Check,
      WarnTriangleFilled
    },

    props: {
      items: {
        type: Object,
        required: true
      },
      name: {
        type: String,
        default: ''
      },
      value: String,
      title: String,
      radioTitle: String,
      errorMessage: String,
      id: {
        type: String,
        default: ''
      },
      classInput: String,
      radioDivClass: String,
      placeholder: {
        type: String,
        default: ''
      },
      frequentItemKeys: {
        type: Array as PropType<string[]>,
        required: false,
        default: () => [],
      },
      frequentGroupLabel: String,
      allGroupLabel: String,
      filterProperty: {
        type: Array,
        required: false,
        default: () => ['', '']
      },
      filter: Boolean,
      disabled: Boolean,
      required: Boolean,
      stateAutoSelected: Boolean,
      defaultTitle: String,
      defaultValue: {
        type: String,
        default: "",
      },
      radioComponentValue: String,
    },

    setup(props) {
      // Access the Vuex store
      const store = useStore();

      // Getters from the store
      const labels = store.getters[`form/${FormGetters.GET_LABELS}`],
          icons = store.getters[`form/${FormGetters.GET_ICONS}`],
          isCar = store.getters[`form/${FormGetters.GET_INPUTS}`].isCar.value,
          filterName = (props.filterProperty[1] as string).toString();

      // Initialize variables with refs
      let frequentItems: Item[] = [],
          value: Ref<string | string[]> = ref(props.defaultValue),
          options = ref<Option[]>([]),
          brands = ref(new Map<string, { label: string; alt: string; value: string; image: string; }>()),
          radioComponentValue = ref(props.radioComponentValue),
          isValid = ref(!(!props.defaultValue || props.defaultValue .trim() === "")),
          items = ref({}),
          isTouched = ref(false),
          disabled = ref(false),
          vehicleType = ref(isCar ? 'car' : 'motorcycle'),
          tmpFrequentItemKeys = ref(props.frequentItemKeys || []),
          stateAutoSelected = ref(false),
          clearable = ref(true),
          showElement = computed(() => useShowElementByPredefinedManufacturer(store, props.name).value),
          isFloatingLabel = ref(false),
          hasBeenMounted = ref(false),
          showError = ref(false),
          wasClickedOnShowResultPage = ref(store.getters[`form/${FormGetters.GET_WAS_CLICKED_ON_SHOW_RESULT_PAGE}`]);

      // Function to reset values
      const setFloatingLabel = () => {
        if (!disabled.value) {
          isFloatingLabel.value = true;
        }
      };

      // Function to reset values
      const resetFloatingLabel = () => {
        if (isEmpty(value.value)) {
          isFloatingLabel.value = false;
        }
      };

      // Default value initialization
      onMounted(async () => {
        // Check if the component should be disabled
        updateDisabled();
        handleClearable();

        // _paq
        if (filterName && value.value) {
          callPaq('trackEvent', 'Gebrauchtfahrzeugwerte', 'verwendete Filter', filterName);
        }
      });

      // Function to get frequent items
      const getFrequentItems = () => {
        if (tmpFrequentItemKeys.value && tmpFrequentItemKeys.value.length > 0 && items.value) {
          let frequentItemList: Item[] = [];
          for (let i = 0; i < Object.keys(items.value).length; i++) {
            if (tmpFrequentItemKeys.value.indexOf(items.value[i].k) === -1) {
              continue;
            }

            frequentItemList.push(items.value[i]);
          }

          frequentItems = frequentItemList;
        }
      };

      // Function to update the selected option value and validity
      const clear = async () => {
        await store.dispatch(`form/${FormActions.SET_IS_RESSETING_FORM}`, true);

        // Reset values
        await resetValues();

        // reset vehicle selected selectbox
        handleVehicleSelected(store, '');

        if (props.name !== 'vehicleSelected') {
          // Get the filter name
          const filterName = store.getters[`form/${FormGetters.GET_INPUTS}`][props.name].apiFilterName;

          // Update store value and filter
          await updateStoreValueAndFilter(store, props, value.value, isValid.value, false, true);

          // Set reset to true in the store
          await store.dispatch(`form/${FormActions.UPDATE_INPUTS}`, {field: props.name, key: 'reset', value: true});

          // Delete filter from the store
          await store.dispatch(`form/${FormActions.DELETE_DATA}`, {field: 'filter', key: 'value', value: filterName});

          // Check if the component should be disabled
          updateDisabled();

          resetInputs(store, getInputsAboveOrder(store, props.name), 'resetValue');
        }

        await setResettingFormToFalseWhenNoRequests(store);
      };

      // Check if the component should be disabled
      const updateDisabled = () => {
        // Disabled
        if (props.disabled) {
          disabled.value = true;
        } else {
          disabled.value = (
              options.value.length === 0 ||
              options.value[1] && options.value[1].options && options.value[1].options.length === 0
          );
        }
      };

      // Check if the component should be clearable
      const handleClearable = () => {
        clearable.value = !stateAutoSelected.value;
      };

      const showErrorMessage = () => {
        showError.value = (isTouched && !isValid) || (wasClickedOnShowResultPage.value && !isValid.value);
      };

      // Function to reset values
      const resetValues = async () => {
        value.value = [];
        isValid.value = false;
        isTouched.value = false;
        stateAutoSelected.value = false;
        radioComponentValue.value = '';
        brands.value = new Map<string, { label: string; alt: string; value: string; image: string; }>();
        disabled.value = false;

        // Dispatch Vuex actions to update the store
        await store.dispatch(`form/${FormActions.UPDATE_INPUTS}`, {field: props.name, key: 'value', value: value.value});
        await store.dispatch(`form/${FormActions.UPDATE_INPUTS}`, {field: props.name, key: 'valid', value: isValid.value});
        await store.dispatch(`form/${FormActions.UPDATE_INPUTS}`, {field: props.name, key: 'isAutoSelected', value: false});

        const filter = store.getters[`form/${FormGetters.GET_DATA}`].filter.value;

        if ((isEmpty(filter) || isEmpty(filter[filterName]))) {
          await store.dispatch(`form/${FormActions.DELETE_DATA}`, {field: 'filter', key: 'value', value: filterName});
        }

        // Reset floating label
        resetFloatingLabel();
      };

      // Watch for changes in the reset value
      watch(() => store.getters[`form/${FormGetters.GET_INPUTS}`][props.name].reset, async (newResetValue, oldResetValue) => {
        // React to the change in the reset value
        if (newResetValue !== oldResetValue && newResetValue) {
          await store.dispatch(`form/${FormActions.UPDATE_INPUTS}`, {field: props.name, key: 'reset', value: false});
        }
      });

      // Watch for changes in the value
      watch(() => store.getters[`form/${FormGetters.GET_INPUTS}`][props.name].value, async (newValue) => {
        if (newValue === 'resetValue') {
          await clear();
        }
      });

      // Add this to the setup function
      watch(stateAutoSelected, (newValue, oldValue) => {
        if (!newValue && oldValue) {
          resetValues();
        }
      });

      // Watch for changes in props.items
      watch(() => props.items, async (newItems) => {
        await itemsWatchFunction(newItems);
      });

      watch(() => store.getters[`form/${FormGetters.GET_WAS_CLICKED_ON_SHOW_RESULT_PAGE}`], async (newValue) => {
        wasClickedOnShowResultPage.value = newValue;
        showErrorMessage();
      });

      const getOptionClass = (label: string) => {
        // Count the number of <br> tags
        const brCount = (label.match(/<br\/>/g) || []).length;

        return [{"el-select-dropdown__item-auto-height" : brCount > 0}];
      }

      const replaceBrWithComma = (input: string) => {
        // Replace <br> and <br/> tags with ", "
        return input.replace(/<br\/>/g, ', ');
      }

      // Function to update the selected value
      const itemsWatchFunction = async (newItems: any) => {
        options.value = [];
        let keysArray: string[] = [];

        const reset = store.getters[`form/${FormGetters.GET_INPUTS}`][props.name].reset;

        // Initialize items
        if (props.filter) {
          items.value = {};

          if (Object.keys(newItems).length !== 0) {
            newItems.forEach((item: any) => {
              if (props.filterProperty && item.key === props.filterProperty[1]) {
                if (item.proposals) {
                  keysArray = item.proposals.map(item => item.k);
                  items.value = item.proposals;
                }
                return;
              }
            });
          }
        } else {
          if (items.value) {
            if (typeof items.value === 'object') {
              keysArray = Object.keys(items.value) as string[];
            }
            if (Array.isArray(items.value)) {
              keysArray = items.value.map(item => item.k);
            }
          }
          items.value = newItems;
        }

        // Check if the 'value' is a non-empty string and if there are keys in 'keysArray'
        if (typeof value.value === 'string') {
          // Determine if the 'value' is present in 'keysArray' and is not equal to '0'
          let isContained = true,
              carsBelowThreshold: CarItem[] = [];

          if (props.name === 'vehicleSelected') {
            carsBelowThreshold = getCarsBelowThreshold(store);

            if (Array.isArray(carsBelowThreshold) && carsBelowThreshold.length > 0) {
              keysArray = carsBelowThreshold.map(item => item.k);
            }
          }

          if (keysArray.length > 0) {
            isContained = value.value !== '0' && keysArray.includes(value.value.toString());
          }

          // If 'value' is not present in 'keysArray' and is a string, reset values
          if (!isContained && isTouched.value) {
            if (props.name === 'vehicleSelected') {
              const tmpCarsBelowThreshold = store.getters[`form/${FormGetters.GET_DATA}`].vehicleSelection.value;
              await resetValues();
              await store.dispatch(`form/${FormActions.UPDATE_DATA}`,{field: 'vehicleSelection', key: 'value', value: tmpCarsBelowThreshold});
            } else {
              await resetValues();
              await updateStoreValueAndFilter(store, props, value.value, isValid.value, false, true);
            }
          }

          if (keysArray.length === 0) {
            await resetValues();
          }
        }

        // if the vehicle type changes, reset the selectbox
        const isCar = store.getters[`form/${FormGetters.GET_INPUTS}`].isCar.value;
        const tmpVehicleType = isCar ? 'car' : 'motorcycle';
        if (vehicleType.value !== tmpVehicleType) {
          vehicleType.value = tmpVehicleType;
          items.value = {};
        }

        // No options available, reset values
        if (isEmpty(items.value)) {
          await resetValues();
        } else {
          // Only one option is available, so select it
          if (Object.entries(items.value).length === 1 && !reset) {
            if ((stateAutoSelected.value === false && isEmpty(value.value)) || stateAutoSelected.value) {
              value.value = items.value[Object.keys(items.value)[0]]['k'];
              isValid.value = true;
              isTouched.value = true;
              stateAutoSelected.value = true;

              const filter = store.getters[`form/${FormGetters.GET_DATA}`].filter.value;

              if (!isEmpty(filter) && !isEmpty(filter[filterName])) {
                stateAutoSelected.value = false;
              }

              await updateStoreValueAndFilter(store, props, value.value, isValid.value, stateAutoSelected.value, store.getters[`form/${FormGetters.GET_INPUTS}`][props.name].reset);
              addNewValueToFunnelStep(store, filterName, value.value);
              setFloatingLabel();
              await validation(store);
            }
          } else {
            stateAutoSelected.value = false;
          }
        }

        // Check if the component is a vehicle component, get the frequent item keys accordingly
        if (props.name === 'vehicle') {
          if (!arraysAreEqual(tmpFrequentItemKeys.value, store.getters[`form/${FormGetters.GET_DATA}`].frequentItems[vehicleType.value])) {
            tmpFrequentItemKeys.value = store.getters[`form/${FormGetters.GET_DATA}`].frequentItems[vehicleType.value];
            await resetValues();
          }
        }

        // Get frequent items
        getFrequentItems();

        if (frequentItems.length > 0) {
          const label = props.frequentGroupLabel ? props.frequentGroupLabel : props.placeholder;
          options.value[0] = {
            value: 'frequent',
            label: label,
            options: frequentItems.map((item) => ({
              value: item.k,
              label: item.v,
            }))
          };

          // Iterate through frequentItems and add items to brandsMap
          frequentItems.forEach((item) => {
            const brandName = item.v.toLowerCase();
            if (icons.brands && icons.brands[brandName]) {
              brands.value.set(item.k, {
                label: '',
                alt: icons.brands[brandName]['alt'],
                value: item.k,
                image: icons.brands[brandName]['src']
              });
            }
          });

          // Create a map to hold the sorted brands
          const sortedBrandsMap = new Map<string, { label: string; alt: string; value: string; image: string; }>();

          // Iterate through sortedKeys and add items to sortedBrandsMap
          tmpFrequentItemKeys.value.forEach((key) => {
            if (brands.value.has(key)) {
              sortedBrandsMap.set(key, brands.value.get(key)!);
            }
          });

          brands.value = sortedBrandsMap;
        }

        if (Array.isArray(items.value)) {
          let optionsTmp = items.value.map((item) => ({
            value: item.k,
            label: item.v,
          }));

          if (items.value.length === 1) {
            const customOption = {
              value: 'reset',
              label: props.defaultTitle + ' ändern',
            };

            const itemsArray = items.value.map((item) => ({
              value: item.k,
              label: item.v,
            }));

            optionsTmp = [customOption].concat(itemsArray);
          }

          options.value[1] = {
            value: 'all',
            label: props.allGroupLabel || '',
            options: optionsTmp,
          };
        }

        // Check if the component should be disabled
        updateDisabled();
        handleClearable();

        if (!isEmpty(value.value)) {
          setFloatingLabel();
        }

        if (!hasBeenMounted.value && props.defaultValue) {
          setFloatingLabel();
          await updateValue(props.defaultValue, false);
          hasBeenMounted.value = true;
        }
      }

      // Function to update the selected value
      const updateValue = async (eventValue: any, fetchNewData: boolean = true) => {
        await store.dispatch(`form/${FormActions.SET_WAS_CLICKED_ON_SHOW_RESULT_PAGE}`, false);

        if (value.value === 'reset' && stateAutoSelected.value) {
          const oldCarsBelowThreshold = store.getters[`form/${FormGetters.GET_DATA}`].vehicleSelection.value;

          if (props.name === 'vehicleSelected') {
            const tmpCarsBelowThreshold = getCarsBelowThreshold(store);

            if (tmpCarsBelowThreshold.length === 0 || tmpCarsBelowThreshold.length > 1) {
              return;
            }

            await store.dispatch(`form/${FormActions.UPDATE_DATA}`,{field: 'vehicleSelection', key: 'value', value: oldCarsBelowThreshold});
          } else {
           await clear();
          }

          // Update the local value
          value.value = items.value[0].k ? items.value[0].k : "";
          return;
        }

        value.value = "";
        isTouched.value = true;

        // Check if eventValue is an instance of Event and contains 'target' and 'target' is an instance of HTMLInputElement
        if (eventValue instanceof Event && 'target' in eventValue && eventValue.target instanceof HTMLInputElement) {
          value.value = eventValue.target.value;

          // Check if radioComponentValue is different from the current value and update if needed
          if (radioComponentValue.value && radioComponentValue.value !== value.value) {
            value.value = radioComponentValue.value;
          }

          setFloatingLabel();
        } else if (eventValue) {
          // Convert eventValue to a string
          value.value = eventValue.toString();

          // Check if value is in the frequentItemKeys and update radioComponentValue accordingly
          if (value.value && typeof value.value === 'string' && tmpFrequentItemKeys.value.includes(value.value)) {
            radioComponentValue.value = value.value;
          } else {
            radioComponentValue.value = '';
          }
        }

        if (value.value === 'reset' && !stateAutoSelected.value) {
           await clear();
           return;
        }

        // Update isValid based on whether the value is empty or not
        isValid.value = !(!value.value || (typeof value.value === "string" && value.value.trim() === ""));

        if (isEmpty(value.value)) {
          await resetValues();

          await store.dispatch(`form/${FormActions.UPDATE_INPUTS}`, {field: props.name, key: 'reset', value: false});
        } else {
          await updateStoreValueAndFilter(store, props, value.value, isValid.value, false, true, false, fetchNewData);

          // _paq
          if (filterName && !store.getters[`form/${FormGetters.GET_IS_RESETTING_FORM}`]) {
            callPaq('trackEvent', 'Gebrauchtfahrzeugwerte', 'verwendete Filter', filterName);
          }
        }

        await validation(store);
        addNewValueToFunnelStep(store, filterName, value.value);
        updateDisabled();
        showErrorMessage();
      }

      const canShowLabel = computed(() => {
          return !props.title || (props.title && frequentItems);
      });

      return {
        icons,
        labels,
        frequentItems,
        options,
        isValid,
        brands,
        updateValue,
        radioComponentValue,
        disabled,
        value,
        isTouched,
        clearable,
        showElement,
        showError,
        clear,
        isFloatingLabel,
        setFloatingLabel,
        resetFloatingLabel,
        getOptionClass,
        replaceBrWithComma,
        canShowLabel
      }
    }
  })
